/**
 * The one place the client talks to the server.
 *
 * There is no HTTP client dependency here on purpose. The contract is `shared/types.ts`, which
 * both sides import, so a hand-rolled fetch is already as typed as a generated one, and it lets
 * the three things that actually matter live in a single readable file:
 *
 *   1. THE ENVELOPE. Every response is `ApiOk` or `ApiErr`. A non-2xx is still JSON with a
 *      player-facing message in voice, so nothing above this layer ever sees an HTTP status.
 *   2. THE ACTION-TOKEN PROTOCOL. A mutating request carries `X-Action-Token` (single use, bound
 *      to an intent) and `Idempotency-Key` (a client-minted UUID). A successful response hands
 *      back `nextToken`, which is why holding a button down is one round trip per press rather
 *      than two.
 *   3. THE CLOCK. `serverNowMs` rides on every response. The client stores the skew and derives
 *      every countdown from it, because a player whose machine is three minutes fast must not
 *      see a bar fill early and then snap back.
 *
 * The client NEVER computes an outcome. It can compute what an outcome WILL COST, because the
 * cost is deterministic and shared, but the roll, the payout and the Heat all arrive from the
 * server or they do not exist.
 */

import type {
  Announcement,
  ApiEnvelope,
  ApiErr,
  ApiErrorCode,
  ApiOk,
  AuthResult,
  ElevateResult,
  BootPayload,
  CatalogPayload,
  Cents,
  ChatLine,
  ChatPage,
  CooloffMethod,
  RicoPanel,
  ScannerPayload,
  CooloffOutcome,
  CrimeOutcome,
  CrimesPayload,
  CrusherOutcome,
  Currency,
  DistrictId,
  EquipOutcome,
  FamilyOverview,
  FamilyPayload,
  FamilyPermission,
  FamilyRole,
  FenceOutcome,
  GatePayload,
  HouseVouchOffer,
  JestLoginResult,
  JestPurchaseResult,
  JestStatus,
  JobOutcome,
  JobsPayload,
  Lineup,
  LaunderOutcome,
  LedgerPage,
  LedgerPayload,
  PurchaseCompletePayload,
  PurchasePayload,
  RevealPayload,
  MorningLine,
  RefillOutcome,
  TerritoryPayload,
  WarBoard,
  WardOutcome,
  WardPayload,
  MarketAisle,
  MarketBuyOutcome,
  MarketCategory,
  MarketPayload,
  MarketSaleOutcome,
  Member,
  MoneyPayload,
  NextPayload,
  PlayerSnapshot,
  Rank,
  RankRequirement,
  RankState,
  RepairOutcome,
  ResendResult,
  ResetConfirmResult,
  ResetRequestResult,
  VerifyResult,
  TierOutcome,
  TreasuryState,
  Upgrade,
  UpgradeId,
  WardrobeItem,
  FightListPayload,
  HitlistPayload,
  PvpIntent,
  PvpOutcome,
  FrontRow,
  FrontUpgradeOutcome,
  PushKind,
  PushPayload,
  PushSubscriptionWire,
} from '@ashport/shared'
import { API_ORIGIN, JEST } from '../build'

export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly retryAfterMs: number | null
  readonly field: string | null

  constructor(e: ApiErr['error']) {
    super(e.message)
    this.name = `ApiError:${e.code}`
    this.code = e.code
    this.retryAfterMs = e.retryAfterMs
    this.field = e.field
  }
}

/** Wall-clock skew against the server, updated from every response. */
let skewMs = 0
export const serverNow = (): number => Date.now() + skewMs
export const skew = (): number => skewMs

/** Set by the store on every envelope, so a screen never has to thread it through. */
type Observer = (envelope: ApiOk<unknown>) => void
let observer: Observer | null = null
export const observe = (fn: Observer | null): void => {
  observer = fn
}

/**
 * PHASE 26. Same origin for every build but Jest's, where the page is served from their CDN and
 * the API stays at ashport.city. `API_ORIGIN` is empty everywhere else, so this is `/api`.
 */
const BASE = API_ORIGIN + '/api'

/*
 * THE BEARER, held in memory for the life of the page. Phase 26, DESIGN-DELTAS 1ag.2.
 *
 * The Jest build calls the API cross-site, where a third-party cookie is at the mercy of the
 * browser and is blocked outright on iOS Safari. So its session rides `Authorization: Bearer`
 * instead: the same token the cookie would hold, minted by `POST /api/jest/login`, resolved by the
 * same middleware. It is never written to storage. A page that reloads asks the SDK for a fresh
 * `playerSigned` and signs in again, which costs one round trip and leaks nothing.
 *
 * Null in every other build, where the `__Host-` cookie carries the session and this header is
 * never sent. `request()` below reads it on every call, and replaces it whenever the server
 * rotates the session and says so in `X-Session-Token`.
 */
let bearer: string | null = null

export function setBearer(token: string | null): void {
  bearer = token
}

export function getBearer(): string | null {
  return bearer
}

/*
 * `ownsToken` WAS HERE, AND IT IS DELETED RATHER THAN FIXED.
 *
 * It meant "this caller keeps its own token bundle, do not publish it to the shared slot", and it
 * existed because the store held ONE slot for whichever board was on screen. It worked for the
 * three boards that set it and it could never have worked for Home, where seven reads fire on
 * mount and each one of them is somebody's "own": the flag can protect a board from the others
 * but there is no value of it that protects seven boards from each other. Laundering, running a
 * job and claiming the Card were all decided by whichever read happened to land last.
 *
 * The store now files every bundle in a registry keyed by intent, so publishing is always correct
 * and withholding is never necessary. See `grants` in `game/src/game/session.ts`.
 */
interface Options {
  method?: 'GET' | 'POST' | 'DELETE'
  body?: unknown
  /** Present on a mutation. The intent is documentation; the DO enforces the binding. */
  token?: string
}

async function request<T>(path: string, o: Options = {}): Promise<ApiOk<T>> {
  const headers: Record<string, string> = {}
  if (o.body !== undefined) headers['Content-Type'] = 'application/json'
  if (o.token) {
    headers['X-Action-Token'] = o.token
    // A retry of the SAME gesture must carry the SAME key, so it is minted here, once, per
    // call. A caller that retries after a network error calls `retry()` with the envelope it
    // already has rather than issuing a fresh request.
    headers['Idempotency-Key'] = crypto.randomUUID()
  }

  if (bearer !== null) headers['Authorization'] = `Bearer ${bearer}`

  let res: Response
  try {
    res = await fetch(BASE + path, {
      method: o.method ?? (o.body === undefined ? 'GET' : 'POST'),
      headers,
      body: o.body === undefined ? undefined : JSON.stringify(o.body),
      // The session is a `__Host-` cookie, and never a bearer token in JavaScript reach, in every
      // build but Jest's. There the cookie is omitted outright, so nothing rides that a
      // third-party cookie policy could drop halfway through a session (1ag.2).
      credentials: JEST ? 'omit' : 'same-origin',
    })
  } catch {
    throw new ApiError({
      code: 'INTERNAL',
      message: 'The line went dead. Try that again.',
      retryAfterMs: 1000,
      field: null,
    })
  }

  // Rotation. The cookie carrier gets a new cookie; the bearer carrier is told in a header the
  // CORS layer exposes by name. Read before the body, because a refusal can rotate too.
  const rotated = res.headers.get('X-Session-Token')
  if (rotated !== null && rotated !== '' && bearer !== null) bearer = rotated

  let envelope: ApiEnvelope<T>
  try {
    envelope = (await res.json()) as ApiEnvelope<T>
  } catch {
    // A non-JSON body from `/api` means the Worker did not handle the route and the Assets
    // Worker served index.html instead. That is `run_worker_first` being wrong, and it is the
    // single most confusing failure in this stack, so it says so.
    throw new ApiError({
      code: 'INTERNAL',
      message: 'The city clerk handed back the wrong form. Reload.',
      retryAfterMs: null,
      field: null,
    })
  }

  skewMs = envelope.serverNowMs - Date.now()
  if (!envelope.ok) throw new ApiError(envelope.error)
  // Every envelope, unconditionally. The player slice and the token bundle both go to the store.
  observer?.(envelope as ApiOk<unknown>)
  return envelope
}

const call = async <T>(path: string, o: Options = {}): Promise<T> =>
  (await request<T>(path, o)).data

/**
 * A mutation returns its outcome AND the next token, because the response to an action is the
 * only place a fresh nonce can be issued without a second round trip.
 */
export interface Mutation<T> {
  data: T
  player: PlayerSnapshot | null
  nextToken: string | null
}

async function mutate<T>(path: string, token: string, body?: unknown): Promise<Mutation<T>> {
  const e = await request<T>(path, { method: 'POST', body: body ?? {}, token })
  return { data: e.data, player: e.player, nextToken: e.nextToken }
}

/* ----------------------------------------------------------------------------- endpoints */

export const api = {
  /**
   * The only read a SIGNED-OUT browser can make, and the reason the Gate can draw a widget at all.
   *
   * `/api/boot` requires a session, so before Phase 13 there was no unauthenticated read anywhere
   * in this API and therefore nowhere for a Turnstile sitekey to come from. That is the mechanical
   * reason the challenge was never wired.
   */
  gate: () => call<GatePayload>('/auth/gate'),

  signup: (b: {
    handle: string
    email: string
    password: string
    turnstile: string
    tosVersion: number
  }) => call<AuthResult>('/auth/signup', { body: b }),

  login: (b: { email: string; password: string }) => call<AuthResult>('/auth/login', { body: b }),

  logout: () => call<{ done: true }>('/auth/logout', { method: 'POST', body: {} }),

  /**
   * RE-ENTER THE PASSWORD, AND THE REASON THIS DID NOT EXIST IS THE POINT.
   *
   * `POST /api/auth/elevate` has existed and worked since Phase 1. `requireElevation()` guards
   * exactly two routes, `POST /family/withdraw` and `POST /market/list`, and both are things a
   * stolen cookie should not be able to do on its own. There was no method here and no control
   * anywhere in the client, so both routes answered NEEDS_ELEVATION to every player who ever
   * tried them, and nothing on any screen could clear it. Emptying a family treasury and listing
   * on the market were unreachable for the entire life of the game.
   *
   * Fifteen minutes, set server side by `ELEVATION_TTL_MS`.
   */
  elevate: (password: string) => call<ElevateResult>('/auth/elevate', { body: { password } }),

  /* ---------------------------------------------------------------- sign in with Google */

  /**
   * Finish a Google sign-in for somebody who does not have an account yet.
   *
   * The START of the flow is NOT here and cannot be: it is a top-level browser navigation to
   * `/api/auth/google/start`, because Google will not be framed and because a `SameSite=Lax` cookie
   * does not survive an XHR round trip. The Gate uses `window.location.assign` for that leg.
   *
   * This is the only leg that carries a value the player typed, which is why it is a POST with an
   * envelope: a taken handle has to come back as a field error on the form they are looking at, and
   * a 302 cannot say that.
   */
  claimGoogle: (b: { token: string; handle: string }) =>
    call<AuthResult>('/auth/google/claim', { body: b }),

  /* ------------------------------------------------------------------ the emailed links */

  /**
   * Confirm an address from the link in the letter.
   *
   * `POST /api/auth/verify` has existed and worked since Phase 1 with NO CALLER ANYWHERE. The
   * account row was created `unverified`, the mail was queued, and there was no path in the client
   * that could consume the token, so every account this game has ever created is unverified.
   *
   * No session is required and that is correct: a player clicking a link from their mail client
   * may well be in a browser they have never signed into.
   */
  verify: (token: string) => call<VerifyResult>('/auth/verify', { body: { token } }),

  /** Send the letter again, for the account whose cookie is on the request. */
  resendVerify: () => call<ResendResult>('/auth/verify/resend', { method: 'POST', body: {} }),

  /**
   * Ask for a reset link. The answer is IDENTICAL whether or not the address exists, so the client
   * must not branch on it either: any difference in what the screen says is the oracle the server
   * went to some trouble to close.
   */
  resetRequest: (email: string, turnstile: string) =>
    call<ResetRequestResult>('/auth/reset/request', { body: { email, turnstile } }),

  /** Set the new password from the link. Every other session is revoked by the server. */
  resetConfirm: (token: string, password: string) =>
    call<ResetConfirmResult>('/auth/reset/confirm', { body: { token, password } }),

  boot: () => call<BootPayload>('/boot'),

  me: () => call<PlayerSnapshot>('/me'),

  /**
   * The Home read, which is also where a Lineup arrives. GAME-DESIGN 13.2.
   *
   * `lineup` is `null` on the overwhelming majority of calls: the challenge rate is zero at the
   * clear rung, which is where nearly every account sits, and the server does not even draw from
   * the keystream in that case.
   */
  home: () => call<PlayerSnapshot>('/me/home'),

  /** The standing Lineup, or a fresh one if the roll says so, or null. See `/api/me/lineup`. */
  lineup: () => call<{ lineup: Lineup | null }>('/me/lineup').then((r) => r.lineup),

  /**
   * Answer the Lineup. The only mutation in the game that carries no action token, because a
   * standing Lineup refuses every tokened verb INCLUDING the read that would mint a fresh one:
   * requiring a token here would mean needing a token to escape the state that stops you getting
   * one. The challenge id is server-issued and single-use, which is what the token would have
   * been for.
   */
  answerLineup: (id: string, pick: number) =>
    call<{ correct: boolean; tookMs: number }>('/me/lineup', { method: 'POST', body: { id, pick } }),

  /** WHAT TO DO NOW, at two scales, in one read. DESIGN-DELTAS 1t.4 and 1t.12. */
  next: () => call<NextPayload>('/next'),

  /** Put the first-night list away, for good, on every device. */
  dismissFirstNight: (token: string) => mutate<{ dismissed: true }>('/next/dismiss', token, {}),

  jobs: () => call<JobsPayload>('/jobs'),

  runJob: (token: string, chainId: DistrictId, step: number, times = 1) =>
    mutate<JobOutcome>('/jobs/run', token, { chainId, step, times }),

  crimes: () => call<CrimesPayload>('/crimes'),

  commitCrime: (token: string, crimeId: string) =>
    mutate<CrimeOutcome>('/crimes/commit', token, { crimeId }),

  setDistrict: (token: string, districtId: DistrictId) =>
    mutate<{ districtId: DistrictId }>('/me/district', token, { districtId }),

  /* ------------------------------------------------------------------------ money */

  money: () => call<MoneyPayload>('/money'),

  /**
   * Your own money history. Every cent of both balances is a row in here.
   *
   * The cursor is BOTH halves. A timestamp alone is not a unique position: laundering writes two
   * rows in one millisecond, and a page boundary inside that millisecond loses the rest of it.
   */
  moneyHistory: (cursor?: { before: number; beforeId: string }, limit = 100) =>
    call<LedgerPage>(
      `/money/ledger?limit=${limit}${cursor ? `&before=${cursor.before}&beforeId=${cursor.beforeId}` : ''}`,
    ),

  /**
   * `expectRateBp` is the rate the SCREEN was showing, sent back so the server can refuse if it
   * has moved. That is what makes "the number on the button is the number that executed" a
   * guarantee rather than a convention: Heat decays, the rate genuinely moves, and being told
   * so beats being quietly charged a different number.
   */
  launder: (token: string, amount: Cents, expectRateBp: number) =>
    mutate<LaunderOutcome>('/money/launder', token, { amount, expectRateBp }),

  buyTier: (token: string, tier: number) => mutate<TierOutcome>('/money/tier', token, { tier }),

  /* ----------------------------------------------------------------- the Morning Line */

  /**
   * The rack, the Card, the banked grants and the spend ceiling, in one read.
   *
   * This file contained the string "refill" exactly ZERO times before Stage 4, while
   * `server/src/routes/money.ts` had the endpoints live, `player.ts` implemented all three of
   * GAME-DESIGN 2.3's earned sources with authored locked reasons, and
   * `tools/acceptance/phase5.mjs` called the endpoint over raw HTTP to keep its bot playing. The
   * harness could reach what the UI could not, which is exactly why the gap survived a phase.
   */
  morningLine: () => call<MorningLine>('/money/morning-line'),

  /** Claim today's place. Unconditional, instant, and it puts a grant in your pocket. */
  claimCard: (token: string) =>
    mutate<{ card: number; granted: boolean; heatCost: number }>('/money/card', token, {}),

  /** Spend a banked grant. The decision, as opposed to the turning up. */
  spendRefill: (token: string, resource: 'energy' | 'nerve') =>
    mutate<RefillOutcome>('/money/refill', token, { resource }),

  fence: (token: string, slotId: string, qty: number, expectFeeBp: number) =>
    mutate<FenceOutcome>('/money/fence', token, { slotId, qty, expectFeeBp }),

  /* ------------------------------------------------------------------------- heat */

  heat: () => call<HeatPayload>('/heat'),

  /* --------------------------------------------------------------------- scanner */

  /** The feed, the wanted list and the district pressure. One read, three shapes. */
  scanner: () => call<ScannerPayload>('/scanner'),

  coolOff: (token: string, method: CooloffMethod, points: number, expectCostPerPoint: number | null) =>
    mutate<CooloffOutcome>('/heat/cooloff', token, { method, points, expectCostPerPoint }),

  /* ----------------------------------------------------------------------- market */

  /** Immutable for a manifest version, so it is fetched once and cached for that version. */
  catalog: () => call<CatalogPayload>('/market/catalog'),

  /** One of the seven books, or `all`, which the server reads across all seven and merges. */
  market: (aisle: MarketAisle) => call<MarketPayload>(`/market/${aisle}`),

  listItem: (token: string, slotId: string, qty: number, unitPrice: Cents) =>
    mutate<MarketSaleOutcome>('/market/list', token, { slotId, qty, unitPrice }),

  /** `maxUnitPrice` is a LIMIT, never a bid: the book fills at the ask or it does not fill. */
  buyItem: (
    token: string,
    category: MarketCategory,
    itemId: string,
    qty: number,
    maxUnitPrice: Cents,
  ) => mutate<MarketBuyOutcome>('/market/buy', token, { category, itemId, qty, maxUnitPrice }),

  cancelListing: (token: string, category: MarketCategory, listingId: string) =>
    mutate<{ listingId: string; returned: number }>('/market/cancel', token, { category, listingId }),

  /**
   * The house's shelf, in dirty. `expectUnitPrice` is the price the CARD was showing.
   *
   * The stock rotates on the hour and the server resolves the price itself, so a board left open
   * across the boundary used to charge the new hour's number under the old hour's button. Same
   * lock as `expectRateBp` on laundering and `expectFeeBp` on the fence.
   */
  buyBlack: (
    token: string,
    category: MarketCategory,
    itemId: string,
    qty: number,
    expectUnitPrice: Cents,
  ) =>
    mutate<{ itemId: string; name: string; qty: number; paid: Cents }>('/market/black', token, {
      category,
      itemId,
      qty,
      expectUnitPrice,
    }),

  /* ------------------------------------------------------------------------- ward */

  /**
   * The Ward: everybody currently in a cell, priced and odds-checked FOR THIS VIEWER.
   *
   * The read carries the token bundle for all four verbs, because the screen renders all four and
   * a token per button would be four rows written for one page view.
   */
  ward: () => call<WardPayload>('/ward'),

  /**
   * The whole map, because the whole map is forty rows.
   *
   * IT CARRIES THE TOKEN BUNDLE NOW. Stage A read and did not write, so this call minted nothing
   * and `tools/acceptance/phase7.mjs` had to borrow a token from `/api/family` to post anything at
   * all. One bundle for the whole screen, which is the house rule: "One token per screen's intent
   * set. Spending it on any intent spends it for all of them."
   */
  territory: () => call<TerritoryPayload>('/territory'),

  /** One block's fight, live: the score, the slots, the Operations. What the War Room reads. */
  warBoard: (blockId: string) => call<WarBoard>(`/territory/board/${blockId}`),

  /** Take the till into the family vault, and settle the bill in the same act. */
  collectTerritory: (token: string) =>
    mutate<{
      collected: Cents
      gross: Cents
      hours: number
      capped: boolean
      upkeepCharged: Cents
      upkeepOwed: Cents
      blocks: number
    }>('/territory/collect', token, {}),

  /** One Books level, at the published price, refused if the price has moved. */
  buyBooks: (token: string, expectCost: number) =>
    mutate<{ books: number; cost: Cents }>('/territory/books', token, { expectCost }),

  /** One tick of doors, up on your own ground and down on somebody else's inside a window. */
  fortify: (token: string, blockId: string) =>
    mutate<{ fortTier: number; fortTierName: string; raised: boolean; score: number }>(
      '/territory/fortify',
      token,
      { blockId },
    ),

  /** Call a crew out over a block, striking one of the four windows out. */
  declareWar: (token: string, blockId: string, strikeSlot: number) =>
    mutate<{ warId: string; windowIndex: number; struckSlot: number }>(
      '/territory/declare',
      token,
      { blockId, strikeSlot },
    ),

  /** The defender names which of the remaining three it resolves in. */
  answerWar: (token: string, warId: string, windowIndex: number) =>
    mutate<{ warId: string; windowIndex: number; label: string }>('/territory/answer', token, {
      warId,
      windowIndex,
    }),

  /** A raid. The published numbers on the button are the numbers the server rolls against. */
  raidBlock: (token: string, blockId: string) =>
    mutate<{
      roll: number
      nerve: number
      knocked: number
      control: number
      chipped: boolean
      captured: boolean
      heldOnScore: boolean
      yourScore: number
      theirScore: number
      flipAt: number
    }>('/territory/raid', token, { blockId }),

  /** Give a block back to neutral, as a public act on the Scanner. */
  cedeBlock: (token: string, blockId: string) =>
    mutate<{ blockId: string; blocksHeld: number }>('/territory/cede', token, { blockId }),

  /** A Capo calls an Operation on a block. */
  callOperation: (token: string, blockId: string) =>
    mutate<{ opId: string; expiresAt: number; commitNerve: number }>(
      '/territory/operation',
      token,
      { blockId },
    ),

  /** ONE TAP. The commit scores the moment it is made, whether or not the leader ever calls it. */
  joinOperation: (token: string, opId: string) =>
    mutate<{ opId: string; nerve: number; score: number; contributors: number; counted: boolean }>(
      '/territory/operation/join',
      token,
      { opId },
    ),

  /** The leader calls it in, or any contributor in the last ten minutes of the window. */
  triggerOperation: (token: string, opId: string) =>
    mutate<{ opId: string; contributors: number; knocked: number; captured: boolean }>(
      '/territory/operation/trigger',
      token,
      { opId },
    ),

  /** Somebody else's bail, in clean cash. Burned to a sink; it never reaches the inmate. */
  bailOut: (token: string, playerId: string) =>
    mutate<WardOutcome>('/ward/bail', token, { playerId }),

  /** Four Nerve against a published chance. No cash gate: the social route is the fast one. */
  bustOut: (token: string, playerId: string) =>
    mutate<WardOutcome>('/ward/bust', token, { playerId }),

  /** Your own way out, in dirty, at a multiple that climbs with every use inside a day. */
  selfBail: (token: string) => mutate<WardOutcome>('/ward/self-bail', token, {}),

  /** Off the list, or back on it. Free and reversible. */
  sealCell: (token: string, on: boolean) =>
    mutate<{ sealed: boolean }>('/ward/seal', token, { on }),

  /* -------------------------------------------------------------------------- PvP */

  /**
   * The Fight List, and everything a userscript would otherwise compute.
   *
   * The win band, the Fair Fight band, the remaining intents, the hospital timer, the armed
   * Ambush and `attackable` with its refusal reason all arrive PRE-COMPUTED, for this viewer, on
   * every row. 1j.12 and DESIGN-DELTAS 1o.2: publishing them removes the reason to run a script
   * at all, and gating them behind a pass would sell exactly that advantage.
   *
   * THE CLIENT DOES NO ARITHMETIC ON THIS SCREEN. Not one number below is derived here.
   */
  pvp: () => call<FightListPayload>('/pvp'),

  /**
   * One of the three intents, DECLARED BEFORE THE ROLL.
   *
   * Mafia Wars and Torn both let the attacker pick the outcome after winning, which turns every
   * attack into a strictly optimal choice. The intent travels in the same request that spends the
   * Nerve and there is no second decision.
   */
  attack: (token: string, targetId: string, intent: PvpIntent) =>
    mutate<PvpOutcome>('/pvp/attack', token, { targetId, intent }),

  /** The board: six tiers, dirty in and dirty out, and its own rules printed beside the rows. */
  hitlist: () => call<HitlistPayload>('/pvp/hitlist'),

  /** Post a bounty, in dirty, at the pot plus a 50 percent fee burned on the way in. */
  postBounty: (token: string, targetId: string, amount: number, note: string) =>
    mutate<{ id: string; amount: Cents; fee: Cents; paid: Cents; tier: number; expiresAt: number }>(
      '/pvp/bounty',
      token,
      { targetId, amount, note },
    ),

  /** Nominate the 6-hour Sleep block. It takes effect 72 hours after it is filed. */
  nominateSleep: (token: string, hour: number) =>
    mutate<{ startHour: number; pendingHour: number; pendingAt: number }>('/pvp/sleep', token, {
      hour,
    }),

  /* ----------------------------------------------------------------------- ledger */

  /**
   * The pass track, the badge wall, the Era record, the standings, the wardrobe and the cap.
   *
   * Still a pure READ and it still carries no action token, which is worth restating now that the
   * screen has live buttons on it. The token for a purchase comes from `/purchase`, which is a
   * separate read with its own bundle, for the reason the `ownsToken` comment above gives at
   * length: a board that mints a token it holds itself does not put it where another board will
   * spend it.
   */
  /*
   * THIS READ MINTS `ledger.equip`, which is what the wardrobe spends.
   *
   * The comment above used to end "Still a pure READ and it still carries no action token". Phase
   * 13 gave the wardrobe a verb. The bundle goes to the store like every other one: it is filed
   * under `ledger.equip` and cannot collide with the `ledger.purchase` bundle `api.purchases()`
   * mints, because a registry keyed by intent has no slot for them to fight over.
   */
  ledger: () => call<LedgerPayload>('/ledger'),

  /**
   * Put something on, or take it off with `cosmeticId: null`.
   *
   * The SLOT is never sent when putting something on. The server reads it out of the entitlement
   * row, which is what makes "you cannot wear what you do not own" a property of the statement
   * rather than a check somebody has to remember. DESIGN-DELTAS 1t.13.
   */
  equipCosmetic: (token: string, cosmeticId: string | null, slot?: string) =>
    mutate<{ wardrobe: WardrobeItem[] }>('/ledger/equip', token, { cosmeticId, slot }),

  /* --------------------------------------------------------------------- purchases */

  /**
   * What this account has spent, what it owns, and the action token a purchase spends.
   *
   * The envelope is returned rather than the payload, because `Ledger.tsx` reads the bundle off it
   * and hands the token straight to `checkout` in the same gesture. A checkout is the one verb
   * that must not take a token out of the registry: it is a two-call handshake and the player is
   * about to leave the page, so the nonce should live and die inside `startCheckout`.
   */
  purchases: () => request<PurchasePayload>('/purchase'),

  /**
   * Start a checkout. Returns the Stripe URL to send the browser to.
   *
   * THE CLIENT NEVER SENDS A PRICE. It sends a SKU and the server reads everything else out of
   * `content/pass.json`, which the content gate has already walked. That is the same seam
   * `equip` uses for item power.
   */
  checkout: (token: string, sku: string) =>
    call<{ url: string; sessionId: string }>('/purchase/checkout', {
      method: 'POST',
      body: { sku },
      token,
    }),

  /**
   * Fulfil, from the landing page.
   *
   * The webhook has almost certainly already settled this: Stripe waits up to ten seconds for a
   * 2xx before redirecting. This calls the same fulfilment function anyway, because "almost
   * certainly" is not a guarantee and the function is safe to call twice.
   */
  purchaseComplete: (sessionId: string) =>
    call<PurchaseCompletePayload>(`/purchase/complete?session_id=${encodeURIComponent(sessionId)}`),

  /**
   * The Reveal: this player's own roll history, plus the Era's commitment and, after the close,
   * the key.
   *
   * Its own read rather than a field on `/ledger`, because it is up to two hundred rows of raw
   * uint32 words and the Ledger is a screen people sit on. It answers all Era, not only at the
   * close: publishing the rows twelve weeks before the key is what makes checking them mean
   * anything.
   */
  reveal: (before = 0) =>
    call<RevealPayload>(`/ledger/reveal${before > 0 ? `?before=${before}` : ''}`),

  /* ------------------------------------------------------------------------- gear */

  gear: () => call<GearPayload>('/gear'),

  equip: (token: string, slotId: string, itemId: string) =>
    mutate<EquipOutcome>('/gear/equip', token, { slotId, itemId }),

  repair: (token: string, slotId: string, points: number) =>
    mutate<RepairOutcome>('/gear/repair', token, { slotId, points }),

  crush: (token: string, slotId: string, itemId: string, qty: number) =>
    mutate<CrusherOutcome>('/gear/crush', token, { slotId, itemId, qty }),

  collectFronts: (token: string) =>
    mutate<{ collected: Cents; hours: number; capped: boolean }>('/gear/collect', token, {}),
  /** PHASE 22. A front's next level. The server prices it; the client only names the row. */
  upgradeFront: (token: string, slotId: string) =>
    mutate<FrontUpgradeOutcome>('/gear/upgrade', token, { slotId }),

  /* ------------------------------------------------------------------- push, Phase 23 */

  /** What this account is listening for. `endpoint` marks this browser's own row. */
  push: async (endpoint: string | null) =>
    (await request<PushPayload>(`/push${endpoint ? `?endpoint=${encodeURIComponent(endpoint)}` : ''}`)).data,
  pushSubscribe: async (sub: PushSubscriptionJSON, kinds: PushKind[]) =>
    (await request<PushSubscriptionWire>('/push/subscribe', { body: { endpoint: sub.endpoint, keys: sub.keys, kinds } })).data,
  pushUnsubscribe: async (endpoint: string) =>
    (await request<{ removed: boolean }>('/push/subscribe', { method: 'DELETE', body: { endpoint } })).data,
  pushPing: async () => (await request<{ queued: boolean }>('/push/ping', { body: {} })).data,

  /* ----------------------------------------------------------------------- family */

  /**
   * The whole Families surface in one request.
   *
   * Roster, applications, upgrades, vault, notices, chat, the treasury log, the audit trail and
   * the sponsorship panel, plus the browser for a player who has no family yet. Four separate
   * reads would be four round trips on a screen people sit on and four chances for the four to
   * disagree with each other.
   */
  family: () => call<FamilyPayload>('/family'),

  /** Scrollback older than the room's own replay ring. Paged on `seq`, which is a total order. */
  familyChat: (beforeSeq: number, limit = 50) =>
    call<ChatPage>(`/family/chat?before=${beforeSeq}&limit=${limit}`),

  createFamily: (token: string, name: string, tag: string, motto: string) =>
    mutate<{ overview: FamilyOverview }>('/family/create', token, { name, tag, motto }),

  applyToFamily: (token: string, familyId: string, pitch: string) =>
    mutate<{ expiresAt: number }>('/family/apply', token, { familyId, pitch }),

  decideApplication: (token: string, playerId: string, accept: boolean) =>
    mutate<{ joined: boolean; member: Member | null }>('/family/decide', token, { playerId, accept }),

  setFamilyRole: (token: string, playerId: string, role: FamilyRole) =>
    mutate<{ member: Member }>('/family/role', token, { playerId, role }),

  setFamilyCrew: (token: string, playerId: string, crewId: string | null) =>
    mutate<{ member: Member }>('/family/crew', token, { playerId, crewId }),

  kickMember: (token: string, playerId: string, reason: string) =>
    mutate<{ removed: string }>('/family/kick', token, { playerId, reason }),

  leaveFamily: (token: string) => mutate<{ removed: string }>('/family/leave', token, {}),

  grantPermission: (token: string, playerId: string, permission: FamilyPermission, grant: boolean) =>
    mutate<{ permissions: FamilyPermission[] }>('/family/grant', token, { playerId, permission, grant }),

  depositToVault: (token: string, currency: Currency, amount: Cents) =>
    mutate<{ amount: Cents; currency: Currency; treasury: TreasuryState }>('/family/deposit', token, {
      currency,
      amount,
    }),

  withdrawFromVault: (token: string, currency: Currency, amount: Cents) =>
    mutate<{ amount: Cents; currency: Currency; treasury: TreasuryState }>('/family/withdraw', token, {
      currency,
      amount,
    }),

  /**
   * `expectCost` is the price the SCREEN was showing, sent back so the server can refuse if it
   * has moved. Same rule as the laundering rate: the published number is the executed number,
   * enforced rather than promised.
   */
  buyFamilyUpgrade: (token: string, upgradeId: UpgradeId, expectCost: Cents) =>
    mutate<{ upgrade: Upgrade; treasury: TreasuryState }>('/family/upgrade', token, {
      upgradeId,
      expectCost,
    }),

  setTithe: (token: string, pct: number) => mutate<{ pct: number }>('/family/tithe', token, { pct }),

  setFamilySettings: (token: string, recruiting: boolean, minLevel: number, motto: string) =>
    mutate<{ recruiting: boolean; minLevel: number; motto: string }>('/family/settings', token, {
      recruiting,
      minLevel,
      motto,
    }),

  postFamilyChat: (token: string, body: string) =>
    mutate<{ line: ChatLine }>('/family/chat', token, { body }),

  announce: (token: string, body: string, pinned: boolean) =>
    mutate<{ announcement: Announcement }>('/family/announce', token, { body, pinned }),

  giftNerve: (token: string, playerId: string) =>
    mutate<{ given: boolean; to: string; cost: Cents }>('/family/gift', token, { playerId }),

  offerVouch: (token: string, playerId: string) =>
    mutate<{ id: string; stake: Cents; expiresAt: number }>('/family/sponsor', token, { playerId }),

  acceptVouch: (token: string, id: string, witnessId: string) =>
    mutate<{ id: string; sponsorId: string; sponsorName: string }>('/family/vouch', token, {
      id,
      witnessId,
    }),

  /**
   * The rank ladder: the rung, the checklist and the floor underneath it.
   *
   * GAME-DESIGN 6.3 makes this a screen and not a boolean: "An unsponsored player at day 7 is
   * capped out of half the game and knows it, BECAUSE THE RANK SCREEN SHOWS THE CHECKLIST."
   */
  /*
   * THE RANK SURFACE MINTS ITS OWN TOKEN, because a player with no family cannot borrow the
   * Families screen's bundle and is exactly the player the ladder exists for.
   *
   * It no longer has to HIDE it. `RANK_INTENTS` and `FAMILY_INTENTS` are disjoint, so the registry
   * holds both at once and the rank panel sitting inside the Families screen is no longer a
   * problem: neither bundle can displace the other because neither covers an intent of the other.
   */
  rank: () => call<RankState>('/rank'),

  claimRank: (token: string) =>
    mutate<{ rank: Rank; earned: Rank; requirements: RankRequirement[] }>('/rank/promote', token, {}),

  /** The NPC Sponsor of Record. Strictly worse than a human vouch, and always available. */
  /** PHASE 24. Draw the Line at Soldier. */
  drawLine: (token: string, line: 'energy' | 'nerve') =>
    mutate<{ line: string; energyMax: number; nerveMax: number }>('/rank/line', token, { line }),
  takeHouseVouch: (token: string) =>
    mutate<RankState & { taken: string; sponsor: HouseVouchOffer }>('/rank/house', token, {}),

  /* ------------------------------------------------------------------- Jest.com, Phase 26 */

  /** Whether this deployment has a Jest game at all. Public; the JestGate reads it on a refusal. */
  jestStatus: () => call<JestStatus>('/jest/status'),

  /**
   * The door the Jest build comes in by. A `playerSigned` from the SDK in, a bearer session out
   * and no cookie. The caller hands the token to `setBearer` and then boots as any build does.
   */
  jestLogin: (playerSigned: string) =>
    call<JestLoginResult>('/jest/login', { method: 'POST', body: { playerSigned } }),

  /**
   * A purchase Jest signed, or the list `getIncompletePurchases` hands back on every start. The
   * server settles each once by its token, so posting the same list twice is safe (1ag.4).
   */
  jestPurchase: (signed: { purchaseSigned: string } | { purchasesSigned: string }) =>
    call<JestPurchaseResult>('/jest/purchase', { method: 'POST', body: signed }),
}

/** What `GET /api/heat` returns. Every number priced against this player at this instant. */
export interface HeatPayload {
  heat: number
  heatReason: string
  heatPeriodMs: number
  floor: number
  clearsThisEra: number
  costPerPoint: Cents
  ricoMultiplier: number
  maxPoints: number
  dirty: Cents
  fronts: number
  cooloffUntil: number
  lieLowMinMs: number
  /**
   * What a payout KEEPS while running legit, in basis points. 5500 means 55 kept and 45 lost.
   *
   * The name reads like the cut and is the opposite of it, which is worth stating here because the
   * first two comments written for this field both said "takes off" and both were wrong.
   */
  legitPayoutBp: number
  /** When running legit may be started again after stopping, or 0 when it may be started now. */
  legitRestartAt: number
  band: string
  label: string
  note: string
  /**
   * The RICO meter and the whole six-rung ladder, names and effect lines included.
   *
   * Every string on it is the writer's, from `content/warrants.json`. None of the six names is
   * typed into a screen, which `tools/content/validate.mjs` enforces against a corpus that now
   * carries them: a rung name written as a literal in `game/src` fails the build.
   */
  rico: RicoPanel
  methods: { method: CooloffMethod; artId: string; available: boolean; lockedReason: string | null }[]
}

/** What `GET /api/gear` returns: the pockets, priced. */
export interface GearRow {
  slotId: string
  itemId: string
  category: MarketCategory
  rarity: string
  name: string
  sub: string
  artId: string
  rarityPlateId: string
  tier: number
  baseValue: Cents
  qty: number
  cond: number | null
  equipped: boolean
  income: Cents | null
  occupancy: number | null
  lockedUntil: number
  power: number
  statMode: 'absolute' | 'modifier' | null
  stats: [number, number, number] | null
  repairCost: Cents
  repairPoints: number
  crusherValue: Cents
  crusherItemId: string | null
  crusherItemName: string | null
  crusherQty: number
}

export interface GearPayload {
  items: GearRow[]
  fronts: number
  till: Cents
  tillHours: number
  tillCapped: boolean
  accrualCapHours: number
  ownCap: number
  /** PHASE 21. Each front on its own. Empty when none is owned. */
  frontRows: FrontRow[]
  /** PHASE 22. How far a front levels. */
  frontLevelCap: number
}
